import { parseBusinessHours } from "~/lib/utils/business-hours";
import {
  getTimezoneFromPostalCode,
  inferPostalCountry,
} from "~/lib/utils/timezone";

export const WEEKDAY_ORDER = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

export type WeekdayShort = (typeof WEEKDAY_ORDER)[number];

export type DaySlot = { open: string; close: string } | null;

/** Canonical weekly schedule stored on ProjectLocation.businessHoursSchedule */
export type BusinessHoursScheduleV1 = {
  version: 1;
  timezone: string | null;
  weekly: Record<WeekdayShort, DaySlot>;
  openDays: WeekdayShort[];
  /** Human-readable one-liner for admin preview */
  displaySummary: string;
};

const FULL_DAY_NAMES: Record<WeekdayShort, string> = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
};

function emptyWeekly(): Record<WeekdayShort, DaySlot> {
  return {
    Sun: null,
    Mon: null,
    Tue: null,
    Wed: null,
    Thu: null,
    Fri: null,
    Sat: null,
  };
}

function formatTime12h(hhmm: string): string {
  const [hRaw, mRaw] = hhmm.split(":");
  const h = parseInt(hRaw ?? "0", 10);
  const m = parseInt(mRaw ?? "0", 10);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const minStr = m > 0 ? `:${m.toString().padStart(2, "0")}` : "";
  return `${hour12}${minStr} ${period}`;
}

function timezoneAbbrev(timezone: string | null | undefined): string {
  if (!timezone) return "local time";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone;
  } catch {
    return timezone;
  }
}

function cityLabelFromTimezone(timezone: string | null | undefined): string {
  if (!timezone) return "";
  const map: Record<string, string> = {
    "America/Toronto": "Toronto",
    "America/New_York": "Eastern",
    "America/Chicago": "Central",
    "America/Denver": "Mountain",
    "America/Los_Angeles": "Pacific",
    "America/Vancouver": "Vancouver",
    "America/Winnipeg": "Winnipeg",
    "America/Halifax": "Halifax",
  };
  return map[timezone] ?? timezone.split("/").pop()?.replace(/_/g, " ") ?? "";
}

export function buildBusinessHoursScheduleFromString(
  businessHoursString: string | null | undefined,
  timezone?: string | null,
): BusinessHoursScheduleV1 | null {
  const raw = businessHoursString?.trim();
  if (!raw) return null;

  const blocks = parseBusinessHours(raw);
  if (blocks.length === 0) return null;

  const weekly = emptyWeekly();
  for (const block of blocks) {
    for (const day of block.days) {
      const key = day as WeekdayShort;
      if (!WEEKDAY_ORDER.includes(key)) continue;
      weekly[key] = { open: block.startTime, close: block.endTime };
    }
  }

  const openDays = WEEKDAY_ORDER.filter((d) => weekly[d] !== null);
  const displaySummary = openDays
    .map((d) => {
      const slot = weekly[d]!;
      return `${d} ${formatTime12h(slot.open)}–${formatTime12h(slot.close)}`;
    })
    .join(", ");

  return {
    version: 1,
    timezone: timezone ?? null,
    weekly,
    openDays,
    displaySummary,
  };
}

export function validateBusinessHoursString(
  businessHoursString: string | null | undefined,
  timezone?: string | null,
): {
  valid: boolean;
  error?: string;
  schedule: BusinessHoursScheduleV1 | null;
  openDays: WeekdayShort[];
} {
  const raw = businessHoursString?.trim();
  if (!raw) {
    return { valid: true, schedule: null, openDays: [] };
  }

  const schedule = buildBusinessHoursScheduleFromString(raw, timezone);
  if (!schedule || schedule.openDays.length === 0) {
    return {
      valid: false,
      error:
        "Could not parse business hours. Use a format like \"Mon-Fri: 7:00 AM - 8:00 PM, Sat: 9:00 AM - 5:00 PM\".",
      schedule: null,
      openDays: [],
    };
  }

  return {
    valid: true,
    schedule,
    openDays: schedule.openDays,
  };
}

export function resolveTimezoneForLocation(opts: {
  zipCode?: string | null;
  city?: string | null;
}): string | null {
  const zip = opts.zipCode?.trim() ?? "";
  if (!zip) return null;
  return getTimezoneFromPostalCode(zip, inferPostalCountry(zip));
}

/** Safe default when AI/scraped hours are present but not parseable — never block location save. */
const FALLBACK_WEEKDAY_HOURS = "Mon-Fri: 8:00 AM - 5:00 PM";

/**
 * Normalize common AI/scrape prose into a string parseBusinessHours accepts.
 * e.g. "Monday to Thursday: 8:30 AM – 5:00 PM, Friday: 8:30 AM – 5:00 PM"
 */
export function normalizeBusinessHoursForSave(raw: string): string {
  let text = raw
    .replace(/\r\n/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/\n+/g, " ")
    .replace(/[\u2013\u2014\u2212]/g, "-")
    .replace(/\bHours\s*:\s*/i, "")
    .replace(/\bBusiness Hours\s*:\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();

  text = text
    .replace(/\b(Monday|Mon)\s*(?:to|-)\s*(Thursday|Thu|Thur|Thurs)\b/gi, "Mon-Thu")
    .replace(/\b(Monday|Mon)\s*(?:to|-)\s*(Friday|Fri)\b/gi, "Mon-Fri")
    .replace(/\b(Monday|Mon)\s*(?:to|-)\s*(Wednesday|Wed)\b/gi, "Mon-Wed")
    .replace(/\b(Tuesday|Tue|Tues)\s*(?:to|-)\s*(Friday|Fri)\b/gi, "Tue-Fri")
    .replace(/\b(Tuesday|Tue|Tues)\s*(?:to|-)\s*(Thursday|Thu|Thurs)\b/gi, "Tue-Thu")
    .replace(/\bMonday\b/gi, "Mon")
    .replace(/\bTuesday\b/gi, "Tue")
    .replace(/\bWednesday\b/gi, "Wed")
    .replace(/\bThursday\b/gi, "Thu")
    .replace(/\bFriday\b/gi, "Fri")
    .replace(/\bSaturday\b/gi, "Sat")
    .replace(/\bSunday\b/gi, "Sun")
    // Drop closed-day clauses — parser ignores them, but they confuse some validators.
    .replace(/,?\s*(?:Sat|Sun|Saturday|Sunday)\s*:\s*Closed\b/gi, "")
    .replace(/\s*:\s*/g, ": ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/,\s*$/, "");

  return text;
}

export function prepareLocationBusinessHoursFields(loc: {
  businessHours?: string | null;
  zipCode?: string | null;
  city?: string | null;
}): {
  businessHours: string | null;
  businessHoursSchedule: BusinessHoursScheduleV1 | null;
} {
  const raw = loc.businessHours?.trim() || "";
  if (!raw) {
    return { businessHours: null, businessHoursSchedule: null };
  }

  const timezone = resolveTimezoneForLocation(loc);
  const candidates = [
    normalizeBusinessHoursForSave(raw),
    raw,
    FALLBACK_WEEKDAY_HOURS,
  ].filter((v, i, arr) => v && arr.indexOf(v) === i);

  for (const hours of candidates) {
    const validation = validateBusinessHoursString(hours, timezone);
    if (validation.valid && validation.schedule) {
      if (hours === FALLBACK_WEEKDAY_HOURS && normalizeBusinessHoursForSave(raw) !== FALLBACK_WEEKDAY_HOURS) {
        console.warn(
          `[business-hours] Using weekday fallback for unparseable hours: "${raw.slice(0, 120)}"`,
        );
      }
      return {
        businessHours: hours,
        businessHoursSchedule: validation.schedule,
      };
    }
  }

  throw new Error(
    'Could not parse business hours. Use a format like "Mon-Fri: 7:00 AM - 8:00 PM, Sat: 9:00 AM - 5:00 PM".',
  );
}

function currentDayAndTimeInTimezone(
  timezone: string | undefined,
  testTime: Date,
): { day: WeekdayShort; time: string } {
  const dayFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
  });
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const dayName = dayFormatter.format(testTime) as WeekdayShort;
  const timeParts = timeFormatter.formatToParts(testTime);
  const hour = timeParts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = timeParts.find((p) => p.type === "minute")?.value ?? "00";

  return {
    day: WEEKDAY_ORDER.includes(dayName) ? dayName : "Sun",
    time: `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`,
  };
}

export function isOpenFromSchedule(
  schedule: BusinessHoursScheduleV1,
  timezone?: string,
  testTime?: Date,
): boolean {
  const now = testTime ?? new Date();
  const tz = timezone ?? schedule.timezone ?? undefined;
  const { day, time } = tz
    ? currentDayAndTimeInTimezone(tz, now)
    : {
        day: WEEKDAY_ORDER[now.getDay()]!,
        time: `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`,
      };

  const slot = schedule.weekly[day];
  if (!slot) return false;
  return time >= slot.open && time <= slot.close;
}

export function getNextOpenFromSchedule(
  schedule: BusinessHoursScheduleV1,
  timezone?: string,
  testTime?: Date,
): string {
  const now = testTime ?? new Date();
  const tz = timezone ?? schedule.timezone ?? undefined;

  for (let i = 0; i <= 7; i++) {
    const checkDate = new Date(now.getTime() + i * 86_400_000);
    const { day, time } = tz
      ? currentDayAndTimeInTimezone(tz, checkDate)
      : {
          day: WEEKDAY_ORDER[checkDate.getDay()]!,
          time: `${checkDate.getHours().toString().padStart(2, "0")}:${checkDate.getMinutes().toString().padStart(2, "0")}`,
        };

    const slot = schedule.weekly[day];
    if (!slot) continue;

    const openLabel = formatTime12h(slot.open);
    const isToday = i === 0;
    const isFutureToday = isToday && time < slot.open;
    const isFutureDay = i > 0;

    if (isFutureToday || isFutureDay) {
      if (i === 0) return `at ${openLabel} today`;
      if (i === 1) return `tomorrow at ${openLabel}`;
      return `${FULL_DAY_NAMES[day]}, ${openLabel}`;
    }
  }

  return "soon";
}

/** Per-day lines for agent prompts and location settings preview */
export function formatBusinessHoursScheduleForDisplay(
  schedule: BusinessHoursScheduleV1,
  opts?: { cityLabel?: string | null },
): string {
  const tzAbbr = timezoneAbbrev(schedule.timezone);
  const city =
    opts?.cityLabel?.trim() ||
    cityLabelFromTimezone(schedule.timezone) ||
    null;
  const suffix = city ? `${tzAbbr} (${city})` : tzAbbr;

  return WEEKDAY_ORDER.map((day) => {
    const slot = schedule.weekly[day];
    const label = FULL_DAY_NAMES[day];
    if (!slot) return `${label}: Closed ${suffix}`;
    return `${label}: ${formatTime12h(slot.open)}–${formatTime12h(slot.close)} ${suffix}`;
  }).join("\n");
}

export function parseStoredBusinessHoursSchedule(
  value: unknown,
): BusinessHoursScheduleV1 | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (record.version !== 1) return null;
  if (!record.weekly || typeof record.weekly !== "object") return null;

  const weekly = emptyWeekly();
  for (const day of WEEKDAY_ORDER) {
    const slot = (record.weekly as Record<string, unknown>)[day];
    if (slot === null || slot === undefined) {
      weekly[day] = null;
      continue;
    }
    if (typeof slot === "object" && slot !== null) {
      const s = slot as { open?: unknown; close?: unknown };
      if (typeof s.open === "string" && typeof s.close === "string") {
        weekly[day] = { open: s.open, close: s.close };
      }
    }
  }

  const openDays = WEEKDAY_ORDER.filter((d) => weekly[d] !== null);
  if (openDays.length === 0) return null;

  return {
    version: 1,
    timezone: typeof record.timezone === "string" ? record.timezone : null,
    weekly,
    openDays,
    displaySummary:
      typeof record.displaySummary === "string"
        ? record.displaySummary
        : openDays.join(", "),
  };
}

export function isLocationOpenNow(opts: {
  businessHours?: string | null;
  businessHoursSchedule?: unknown;
  timezone?: string | null;
  testTime?: Date;
}): boolean {
  const schedule =
    parseStoredBusinessHoursSchedule(opts.businessHoursSchedule) ??
    buildBusinessHoursScheduleFromString(
      opts.businessHours,
      opts.timezone ?? null,
    );

  if (schedule) {
    return isOpenFromSchedule(
      schedule,
      opts.timezone ?? schedule.timezone ?? undefined,
      opts.testTime,
    );
  }

  return false;
}

export function getLocationNextOpenTime(opts: {
  businessHours?: string | null;
  businessHoursSchedule?: unknown;
  timezone?: string | null;
  testTime?: Date;
}): string | null {
  const schedule =
    parseStoredBusinessHoursSchedule(opts.businessHoursSchedule) ??
    buildBusinessHoursScheduleFromString(
      opts.businessHours,
      opts.timezone ?? null,
    );

  if (!schedule) return null;

  const tz = opts.timezone ?? schedule.timezone ?? undefined;
  const isOpen = isOpenFromSchedule(schedule, tz, opts.testTime);
  if (isOpen) return null;

  return getNextOpenFromSchedule(schedule, tz, opts.testTime);
}

/** Today's open/close slot in the location timezone (null if closed all day). */
export function getTodayBusinessHoursSlot(
  schedule: BusinessHoursScheduleV1,
  timezone?: string | null,
  testTime?: Date,
): DaySlot | null {
  const tz = timezone ?? schedule.timezone ?? undefined;
  const now = testTime ?? new Date();
  const { day } = tz
    ? currentDayAndTimeInTimezone(tz, now)
    : { day: WEEKDAY_ORDER[now.getDay()]! };
  return schedule.weekly[day] ?? null;
}

/** Widget header range, e.g. "7:00 am - 8:00 pm". */
export function formatWidgetHoursRange(open: string, close: string): string {
  const formatLower = (hhmm: string) =>
    formatTime12h(hhmm).replace(/\s(AM|PM)$/i, (_m, p) => ` ${String(p).toLowerCase()}`);
  return `${formatLower(open)} - ${formatLower(close)}`;
}
